import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';

const evEquipmentTypes = [
  {
    icon: 'ri-charging-pile-2-line',
    label: 'Level 2 Charging Stations',
    desc: 'Wall-mounted or pedestal Level 2 EV chargers (240V) from all brands — ChargePoint, Blink, ClipperCreek, JuiceBox, and more.',
  },
  {
    icon: 'ri-charging-pile-line',
    label: 'DC Fast Charging Stations',
    desc: 'High-power DC fast chargers (Level 3) including CCS, CHAdeMO, and Tesla Supercharger-compatible units.',
  },
  {
    icon: 'ri-battery-2-charge-line',
    label: 'EV Charger Power Modules',
    desc: 'Internal power conversion modules, rectifiers, inverters, and transformer units removed from decommissioned stations.',
  },
  {
    icon: 'ri-cpu-line',
    label: 'Charger Control Boards & Electronics',
    desc: 'Main control boards, payment terminals, LCD screens, RFID readers, and network communication modules.',
  },
  {
    icon: 'ri-plug-line',
    label: 'Charging Cables & Connectors',
    desc: 'Used or damaged charging cables, plugs, and connector assemblies including J1772, CCS, and CHAdeMO types.',
  },
  {
    icon: 'ri-shield-flash-line',
    label: 'EV Charger Enclosures & Cabinets',
    desc: 'Metal enclosures, pedestal housings, cable management systems, and weatherproof cabinets from retired units.',
  },
  {
    icon: 'ri-battery-charge-line',
    label: 'Onboard Charger Units',
    desc: 'Onboard charger modules from electric vehicles — AC-to-DC converters and related power electronics.',
  },
  {
    icon: 'ri-plug-line',
    label: 'Home EV Chargers (Wall Units)',
    desc: 'Residential wall-mounted chargers including Tesla Wall Connector, ChargePoint Home Flex, and third-party Level 2 units.',
  },
];

const whyRecycle = [
  {
    icon: 'ri-earth-line',
    title: 'Protect the Environment',
    desc: 'EV chargers contain copper, aluminum, lithium, and rare earth metals. Recycling prevents toxic materials from entering LA landfills and waterways.',
  },
  {
    icon: 'ri-recycle-line',
    title: 'Recover Valuable Metals',
    desc: 'Copper wiring alone in a DC fast charger can be worth hundreds of dollars. We recover 95%+ of recyclable materials for reuse.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'California Compliant',
    desc: 'We follow CalRecycle CEWD-127531 standards and California e-waste laws (SB 20/50) for safe, documented recycling of all EV infrastructure.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Certificate of Recycling',
    desc: 'Receive official documentation proving your EV charging equipment was recycled responsibly — essential for sustainability reporting.',
  },
];

const faqs = [
  {
    q: 'Do you offer free pickup for EV charging stations?',
    a: 'Yes! We provide FREE pickup anywhere in Los Angeles and surrounding areas when you have 3 or more EV charging stations to recycle. This includes Level 2 chargers, DC fast chargers, and mixed loads of EV infrastructure equipment.',
  },
  {
    q: 'What types of EV charging equipment do you accept?',
    a: 'We accept all types — Level 2 wall/pedestal chargers, DC fast chargers (CCS, CHAdeMO), power modules, control boards, charging cables, enclosures, onboard chargers, and residential wall units from all manufacturers including ChargePoint, Blink, Tesla, ClipperCreek, and JuiceBox.',
  },
  {
    q: 'Do I need to remove the chargers from the wall before pickup?',
    a: 'Not necessarily. Our certified technicians can safely disconnect and remove wall-mounted or pedestal chargers on-site during pickup. If you prefer to have them pre-removed, that works too — we will take them either way.',
  },
  {
    q: 'Can you recycle damaged or non-working chargers?',
    a: 'Absolutely. We accept EV charging equipment in any condition — working, damaged, water-damaged, or completely non-functional. Our recycling process breaks down units to recover valuable metals and components regardless of condition.',
  },
  {
    q: 'What areas do you serve for EV charger recycling?',
    a: 'We serve all of Los Angeles County and much of Southern California. Free pickup for 3+ chargers is available across Downtown LA, Santa Monica, Burbank, Glendale, Pasadena, Long Beach, Torrance, Inglewood, Van Nuys, and surrounding cities.',
  },
  {
    q: 'Will I receive recycling documentation?',
    a: 'Yes. Upon request, we provide a Certificate of Recycling that documents the responsible disposal of your EV charging equipment. This is useful for corporate sustainability reports, LEED certification, and compliance audits.',
  },
];

const EvChargingRecyclingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

  return (
    <>
      <title>EV Charger Recycling Los Angeles | Charging Station Recycling | Free Pickup 3+ | Greenly</title>
      <meta name="description" content="Recycle EV charging stations in Los Angeles — Level 2, DC fast chargers, power modules & cables. FREE pickup for 3+ chargers anywhere in LA. CalRecycle certified. Call (213) 600-1000." />
      <meta name="keywords" content="EV charger recycling Los Angeles, charging station recycling LA, EVSE recycling, electric vehicle charger disposal LA, free EV charger pickup" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/ev-charging-recycling`} />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${SITE_URL}/ev-charging-recycling#webpage`,
            url: `${SITE_URL}/ev-charging-recycling`,
            name: 'EV Charger Recycling Los Angeles | Charging Station Recycling | Free Pickup 3+ | Greenly',
            description: 'Recycle EV charging stations in Los Angeles. FREE pickup for 3+ chargers. CalRecycle certified.',
            isPartOf: { '@id': `${SITE_URL}/#website` },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
                { '@type': 'ListItem', position: 2, name: 'EV Charger Recycling Los Angeles', item: `${SITE_URL}/ev-charging-recycling` },
              ],
            },
          },
          {
            '@type': 'Service',
            '@id': `${SITE_URL}/ev-charging-recycling#service`,
            name: 'EV Charger Recycling Los Angeles',
            description: 'Recycle EV charging stations, Level 2 chargers, DC fast chargers, and all EV infrastructure in Los Angeles. FREE pickup for 3+ chargers.',
            url: `${SITE_URL}/ev-charging-recycling`,
            provider: { '@id': `${SITE_URL}/#business` },
            areaServed: { '@type': 'AdministrativeArea', name: 'Los Angeles County' },
            serviceType: 'EV Charging Equipment Recycling',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free pickup for 3 or more EV charging stations anywhere in Los Angeles' },
          },
          {
            '@type': 'FAQPage',
            '@id': `${SITE_URL}/ev-charging-recycling#faq`,
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          },
        ],
      })}</script>

      <Navbar />
      <main>

        {/* ── HERO ── */}
        <section className="relative bg-gray-900 pt-24 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/Free EV Charger Pickup.jpg"
              alt="EV charging station recycling in Los Angeles — Level 2 and DC fast chargers accepted"
              className="w-full h-full object-cover object-top opacity-30"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-gray-900/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'EV Charger Recycling Los Angeles' }]} />
            <div className="max-w-2xl mt-8">
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  <i className="ri-charging-pile-line"></i>EV Charger Recycling
                </span>
                <span className="inline-flex items-center gap-1.5 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  <i className="ri-truck-line"></i>FREE Pickup — 3+ Stations
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                EV Charger Recycling<br />
                <span className="text-[#74C69D]">in Los Angeles</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                We recycle <strong className="text-white">Level 2 chargers, DC fast chargers, power modules, cables, control boards, and all EV charging infrastructure</strong> — serving all of Los Angeles and surrounding areas. <span className="text-amber-400 font-bold">FREE pickup for 3 charging stations or more.</span>
              </p>
              <div className="flex flex-wrap gap-3 mb-7">
                <a
                  href="tel:+12136001000"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-fill"></i>(213) 600-1000
                </a>
                <Link
                  to="/pickup-request"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-truck-line"></i>Request Free Pickup
                </Link>
              </div>
              <div className="flex flex-wrap gap-5">
                {['All EV Charger Types', 'Free Pickup (3+)', 'CalRecycle Certified', 'Certificate Provided'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-green-300 text-xs font-semibold">
                    <i className="ri-check-line text-[#74C69D]"></i>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── GREEN TRUST BAR ── */}
        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              {[
                { icon: 'ri-map-pin-line', text: 'All of LA County & Surrounding Areas' },
                { icon: 'ri-charging-pile-2-line', text: 'Level 2 & DC Fast Chargers' },
                { icon: 'ri-truck-line', text: 'Free Pickup for 3+ Stations' },
                { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
              ].map((i) => (
                <div key={i.text} className="flex items-center gap-2">
                  <i className={`${i.icon} text-[#74C69D]`}></i>
                  <span className="font-semibold">{i.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FREE PICKUP CALLOUT ── */}
        <section className="py-14 bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="w-full lg:w-1/2">
                <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                  <i className="ri-truck-line"></i>FREE PICKUP PROGRAM
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                  Free EV Charger Pickup<br />
                  <span className="text-amber-600">for 3 Stations or More</span>
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Decommissioning an EV charging site? Upgrading to newer chargers? We come to you — at no charge. Whether it is a parking lot with multiple Level 2 units, a DC fast charging station, or a mixed load of EVSE equipment, if you have <strong className="text-gray-900">3 charging stations or more</strong>, we pick them up free of charge anywhere in Los Angeles.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    'Commercial parking lots & garages',
                    'Shopping centers & retail plazas',
                    'Apartment & condo complexes',
                    'Workplace & corporate campuses',
                    'Municipal & government facilities',
                    'EV charging network operators',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-amber-500 font-bold"></i>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+12136001000"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-phone-fill"></i>Call to Schedule Pickup
                  </a>
                  <Link
                    to="/pickup-request"
                    className="inline-flex items-center gap-2 bg-white border border-amber-300 text-amber-700 font-bold px-6 py-3 rounded-md hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>Fill Out Request Form
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20recycling%20crew%20loading%20decommissioned%20EV%20charging%20stations%20onto%20a%20flatbed%20truck%20in%20Los%20Angeles%2C%20multiple%20Level%202%20and%20DC%20fast%20charger%20units%20being%20carefully%20handled%2C%20workers%20in%20green%20safety%20vests%2C%20organized%20equipment%20removal%20from%20a%20commercial%20parking%20lot%2C%20bright%20sunny%20day%2C%20documentary%20photography%20style%20with%20amber%20and%20green%20color%20accents&width=700&height=480&seq=evcharge002&orientation=landscape"
                    alt="Free EV charger pickup service in Los Angeles for 3 or more charging stations"
                    className="w-full h-72 object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/95 rounded-xl px-5 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl shrink-0">
                        <i className="ri-truck-line text-white text-lg"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">FREE Pickup — No Hidden Fees</div>
                        <div className="text-gray-500 text-xs">3+ charging stations · All of Los Angeles</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── EQUIPMENT TYPES ── */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Accept</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                All EV Charging Equipment<br />
                <strong className="text-[#1B6B3A]">Recycled in Los Angeles</strong>
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
                From a single home wall unit to an entire commercial charging site — we handle all EVSE equipment safely and responsibly.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {evEquipmentTypes.map((t) => (
                <div key={t.label} className="bg-[#F4FAF7] rounded-xl p-6 flex flex-col items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl shrink-0 border border-gray-100">
                    <i className={`${t.icon} text-[#1B6B3A] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{t.label}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY RECYCLE ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4">
                {whyRecycle.map((f) => (
                  <div key={f.title} className="bg-white rounded-xl p-5">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#1B6B3A] rounded-lg mb-3">
                      <i className={`${f.icon} text-white text-sm`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="w-full lg:w-1/2">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Why It Matters</div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5">
                  Why Recycle <strong>EV Charging Equipment</strong><br />in Los Angeles?
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  As California leads the nation in EV adoption, thousands of charging stations are being upgraded, relocated, or decommissioned every year. These units contain <strong className="text-gray-900">high-value copper, aluminum, circuit boards, and lithium components</strong> that should never end up in a landfill.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  A single DC fast charger can contain over <strong className="text-gray-900">200 pounds of copper wiring</strong> and significant amounts of recoverable aluminum and steel. Proper recycling recovers these materials while preventing hazardous e-waste from contaminating LA&apos;s environment.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Greenly is CalRecycle certified to handle EVSE (Electric Vehicle Supply Equipment) recycling safely and in full compliance with California environmental regulations.
                </p>
                <Link
                  to="/pickup-request"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-truck-line"></i>Request Charger Pickup
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Simple Process</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">How EV Charger Recycling Works</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '01',
                  icon: 'ri-phone-line',
                  title: 'Call or Submit',
                  desc: 'Call (213) 600-1000 or fill out our pickup request form. Tell us how many chargers and what types.',
                },
                {
                  step: '02',
                  icon: 'ri-calendar-check-line',
                  title: 'We Schedule',
                  desc: 'We confirm your location, charger count, and a pickup time that works for you anywhere in LA.',
                },
                {
                  step: '03',
                  icon: 'ri-truck-line',
                  title: 'We Pick Up Free',
                  desc: 'Our certified team arrives with the right equipment and loads your chargers — completely free for 3+ units.',
                },
                {
                  step: '04',
                  icon: 'ri-recycle-line',
                  title: 'Certified Recycling',
                  desc: 'We break down and recycle all components per California regulations. Certificate provided on request.',
                },
              ].map((s) => (
                <div key={s.step} className="flex flex-col items-start">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className={`${s.icon} text-white text-lg`}></i>
                    </div>
                    <span className="text-3xl font-black text-gray-100">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SERVICE AREAS ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Coverage Area</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                EV Charger Recycling Across<br />
                <strong className="text-[#1B6B3A]">Los Angeles &amp; Surrounding Cities</strong>
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                We serve the entire LA metro area — free pickup for 3+ EV charging stations anywhere in the region.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
              {[
                'Downtown Los Angeles', 'Hollywood', 'Silver Lake', 'Santa Monica',
                'Venice', 'Culver City', 'West Los Angeles', 'Burbank',
                'Glendale', 'Pasadena', 'Monrovia', 'Long Beach',
                'Torrance', 'Redondo Beach', 'Hawthorne', 'Inglewood',
                'Van Nuys', 'Sherman Oaks', 'North Hollywood', 'Studio City',
              ].map((area) => (
                <div key={area} className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-lg text-xs font-medium text-gray-700">
                  <i className="ri-map-pin-line text-[#1B6B3A] text-xs"></i>{area}
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-sm">
                Don&apos;t see your area? Call <a href="tel:+12136001000" className="text-[#1B6B3A] font-semibold hover:underline">(213) 600-1000</a> — we likely serve it.
              </p>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">EV Charger Recycling FAQs — Los Angeles</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-[#F4FAF7] border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-[#E8F5EE] transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 bg-white">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CROSS-LINKS ── */}
        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Explore Our Other Services</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: 'Battery Recycling', path: '/battery-recycling', icon: 'ri-battery-charge-line' },
                { label: 'TV Recycling', path: '/tv-recycling', icon: 'ri-tv-line' },
                { label: 'Computer Recycling', path: '/computer-recycling', icon: 'ri-computer-line' },
                { label: 'Hard Drive Destruction', path: '/hard-drive-destruction', icon: 'ri-hard-drive-line' },
                { label: 'All Electronics', path: '/services', icon: 'ri-recycle-line' },
              ].map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-5 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={s.icon}></i>{s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 bg-[#1B6B3A] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20green%20background%20with%20subtle%20EV%20charging%20station%20silhouettes%20and%20circuit%20patterns%2C%20minimal%20geometric%20overlay%2C%20soft%20green%20glow%20effects%2C%20tech-forward%20texture&width=1440&height=400&seq=evcharge003&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
              aria-hidden="true"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              <i className="ri-truck-line"></i>FREE PICKUP · 3+ CHARGERS
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Recycle Your EV Charging Stations?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Don&apos;t let decommissioned chargers sit unused or end up in a landfill. We handle everything from pickup to certified recycling — and it is completely free for 3 stations or more anywhere in Los Angeles.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>Request Free Pickup
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-line"></i>Get Directions
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default EvChargingRecyclingPage;